import { useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PublicInquiryCancel } from '@kontor/shared';
import { api, ApiError } from '../api';

const failText = (e: unknown) => (e instanceof ApiError ? (e.status === 429 ? 'Zu viele Versuche. Bitte warten Sie einige Minuten.' : e.message) : 'Das hat nicht geklappt. Bitte versuchen Sie es noch einmal.');

function Frame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="center-screen">
      <div className="card narrow">
        <h1>{title}</h1>
        {children}
      </div>
    </div>
  );
}

function Contact({ info }: { info: PublicInquiryCancel }) {
  if (!info.phone && !info.email) return null;
  return (
    <p className="muted">
      Sie erreichen {info.company}
      {info.phone && <> unter <a href={`tel:${info.phone.replace(/[^\d+]/g, '')}`}>{info.phone}</a></>}
      {info.phone && info.email && ' oder '}
      {info.email && <>{!info.phone && ' unter '}<a href={`mailto:${info.email}`}>{info.email}</a></>}.
    </p>
  );
}

/**
 * Öffentliche Seite hinter dem Link „Anfrage stornieren“ in der Eingangsbestätigung. Erst der Klick auf die Schaltfläche storniert, nicht schon das Öffnen des Links,
 * damit Virenscanner und Linkvorschauen der Mailprogramme keine Anfrage zurückziehen.
 */
export function PublicInquiryCancelPage() {
  const { token = '' } = useParams();
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const key = ['inquiry-cancel', token];
  const info = useQuery({ queryKey: key, queryFn: async () => (await api<{ data: PublicInquiryCancel }>('GET', `/public/inquiries/cancel/${token}`)).data, retry: false });
  const cancel = useMutation({
    mutationFn: async () => (await api<{ data: PublicInquiryCancel }>('POST', `/public/inquiries/cancel/${token}`)).data,
    onSuccess: (d) => client.setQueryData(key, d),
    onError: (e) => setError(failText(e)),
  });
  if (info.isLoading) return <div className="center-screen">Wird geladen …</div>;
  if (info.isError || !info.data) {
    return (
      <Frame title="Link nicht gültig">
        <p className="muted">Dieser Link ist ungültig. Bitte nutzen Sie den Link aus der E-Mail, die Sie nach Ihrer Anfrage von uns bekommen haben.</p>
      </Frame>
    );
  }
  const d = info.data;
  if (d.state === 'cancelled') {
    return (
      <Frame title="Anfrage storniert">
        <p>Ihre Anfrage <strong>{d.reference}</strong> bei {d.company} ist storniert. Es wird nichts weiter veranlasst. Eine neue Anfrage können Sie jederzeit auf der Webseite stellen.</p>
      </Frame>
    );
  }
  if (d.state === 'locked') {
    return (
      <Frame title="Anfrage wird schon bearbeitet">
        <p>Zu Ihrer Anfrage <strong>{d.reference}</strong> hat {d.company} bereits ein Angebot oder einen Auftrag vorbereitet. Bitte melden Sie sich direkt dort, wenn Sie etwas ändern oder absagen möchten.</p>
        <Contact info={d} />
      </Frame>
    );
  }
  return (
    <Frame title="Anfrage stornieren?">
      <p>Möchten Sie Ihre {d.typeLabel} <strong>{d.reference}</strong> bei {d.company} zurückziehen? Wir bearbeiten sie dann nicht weiter.</p>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <button type="button" className="btn btn--primary" onClick={() => { setError(null); cancel.mutate(); }} disabled={cancel.isPending}>{cancel.isPending ? 'Wird storniert …' : 'Ja, Anfrage stornieren'}</button>
    </Frame>
  );
}
